import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export type DownloadInfoStatus = 'idle' | 'starting' | 'downloading' | 'completed' | 'error' | 'cancelled';

export interface DownloadInfoState {
    status: DownloadInfoStatus;
    fileName: string;
    progressPercent: number | null;
    downloadedBytes: number;
    totalBytes: number | null;
    errorMessage: string | null;
}

export const createIdleDownloadInfoState = (): DownloadInfoState => ({
    status: 'idle',
    fileName: '',
    progressPercent: null,
    downloadedBytes: 0,
    totalBytes: null,
    errorMessage: null,
});

const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const precision = value >= 100 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

interface DownloadInfoCardProps {
    state: DownloadInfoState;
    onDismiss: () => void;
    onCancel: () => void;
}

const DownloadInfoCard: React.FC<DownloadInfoCardProps> = ({ state, onDismiss, onCancel }) => {
    if (state.status === 'idle') {
        return null;
    }

    const statusLabel = (() => {
        switch (state.status) {
            case 'starting':
                return 'Preparing download';
            case 'downloading':
                return 'Downloading file';
            case 'completed':
                return 'Download started';
            case 'error':
                return 'Download failed';
            case 'cancelled':
                return 'Download cancelled';
            default:
                return '';
        }
    })();

    const subtitle = (() => {
        switch (state.status) {
            case 'starting':
                return state.fileName || 'Contacting server...';
            case 'downloading':
                if (state.totalBytes && state.totalBytes > 0) {
                    return `${formatBytes(state.downloadedBytes)} of ${formatBytes(state.totalBytes)}`;
                }
                if (state.downloadedBytes > 0) {
                    return `${formatBytes(state.downloadedBytes)} downloaded`;
                }
                return state.fileName || 'Downloading...';
            case 'completed':
                return state.fileName || 'Your browser should open save/download now.';
            case 'error':
                return state.errorMessage || 'Something went wrong.';
            case 'cancelled':
                return state.fileName || 'Download was cancelled.';
            default:
                return '';
        }
    })();

    const canCancel = state.status === 'starting' || state.status === 'downloading';
    const showProgressNumber = state.status === 'downloading' && state.progressPercent !== null;

    return (
        <Box
            sx={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 12,
                zIndex: 20,
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                boxShadow: '0px 16px 36px rgba(15, 23, 42, 0.12)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                }}
            >
                <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {(state.status === 'starting' || state.status === 'downloading') && (
                        state.progressPercent == null ? (
                            <CircularProgress size={24} thickness={4} sx={{ color: '#1A1A1A' }} />
                        ) : (
                            <CircularProgress
                                size={24}
                                thickness={4}
                                variant="determinate"
                                value={state.progressPercent}
                                sx={{ color: '#1A1A1A' }}
                            />
                        )
                    )}
                    {state.status === 'completed' && (
                        <CheckCircleIcon sx={{ fontSize: 22, color: '#22C55E' }} />
                    )}
                    {state.status === 'error' && (
                        <ErrorOutlineIcon sx={{ fontSize: 22, color: '#EF4444' }} />
                    )}
                    {state.status === 'cancelled' && (
                        <ErrorOutlineIcon sx={{ fontSize: 22, color: '#9CA3AF' }} />
                    )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#1A1A1A',
                            lineHeight: 1.3,
                        }}
                    >
                        {statusLabel}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: '#6B7280',
                            lineHeight: 1.3,
                            mt: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {showProgressNumber && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6B7280',
                                minWidth: 34,
                                textAlign: 'right',
                            }}
                        >
                            {state.progressPercent}%
                        </Typography>
                    )}
                    <IconButton
                        size="small"
                        onClick={canCancel ? onCancel : onDismiss}
                        sx={{
                            color: '#9CA3AF',
                            padding: '4px',
                            '&:hover': {
                                color: canCancel ? '#EF4444' : '#6B7280',
                                backgroundColor: canCancel ? '#FEF2F2' : '#F3F4F6',
                            },
                        }}
                        title={canCancel ? 'Cancel download' : 'Dismiss'}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default DownloadInfoCard;
